/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import {
  Box,
  Button,
  CardMedia,
  Grid,
  LinearProgress,
  makeStyles,
  Typography,
} from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { SavedFile } from '../interfaces';
import { gdprFileExtensions, transferFileExtensions } from '../lib/config/file-extension-types';
import { allowedMimeTypes, excludeFromTransfer } from '../lib/config/mime-types';
import theme from '../styles/theme';
import { FileUploadCase } from '../use-case/client/file-upload';
import { removeFileCase } from '../use-case/client/remove-file';
import { RecaptchaCheckCase } from '../use-case/client/recaptcha-check';

const useStyles = makeStyles(() => ({
  dropzone: {
    minHeight: 655,
    maxWidth: 579,
    border: '1px dashed',
    borderColor: (props: StyleProps) => (props.fileErrorStyle ? 'red' : '#00000080'),
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '4px',
    flex: '1 1 auto',
    marginTop: 30,
    cursor: 'pointer',
  },
  dropzoneContainer: {
    height: '100%',
    maxWidth: 579,
  },
  dropzoneText: {
    fontFamily: 'Inter, sans-serif',
    fontSize: '1.25rem',
    letterSpacing: '0.19px',
    lineHeight: '1.24em',
    color: '#707070',
    width: 200,
    textAlign: 'center',
  },
  dropzoneSpan: {
    fontFamily: 'Inter, sans-serif',
    fontSize: '1.25rem',
    letterSpacing: '0.19px',
    lineHeight: '1.24em',
    color: '#E20815',
  },
  arrowIcon: {
    height: '116px',
    width: '120px',
    marginTop: '40px',
  },
  fileError: {
    color: 'red',
  },
  loadginText: {
    color: '#707070',
    fontSize: '0.75rem',
    letterSpacing: '0.12px',
    marginBottom: '20px',
    wordBreak: 'break-all',
  },
  finishedText: {
    color: '#4B71EF',
    fontSize: '0.75rem',
    letterSpacing: '0.12px',
    marginBottom: '20px',
    wordBreak: 'break-all',
  },
  uploadPercent: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 900,
    marginRight: '10px',
  },

  mobileFileButton: {
    color: 'white',
    backgroundColor: '#880226',
    height: '48px',
    width: '214px',
    '&:hover': {
      backgroundColor: 'rgba(136, 2, 38,0.9)',
    },
  },
  deleteButton: {
    borderRadius: '40px',
    paddingLeft: '15px',
    paddingRight: '15px',
    marginLeft: '10px',
    border: '0.5px solid #B1B8C0',
    background: '#B1B8C026',
    height: '20px',
    color: '#707070',
    textTransform: 'uppercase',
    fontFamily: 'Inter, sans-serif',
    fontSize: '0.625rem',
    cursor: 'pointer',
  },
  progressContainer: {
    height: '20px',
    width: '100%',

    display: 'flex',
    flexDirection: 'row',
    marginTop: '10px',
    alignContent: 'center',
  },
  progressBar: {
    height: '100%',
    width: '100%',
  },
  progressColor: { background: '#4B71EF' },
  progressLabel: {
    fontSize: '0.625rem',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 'bold',
    textAlign: 'end',
  },
  fileExtensions: { fontSize: '0.75rem' },
  fileExtensionsGDPR: { fontSize: '1rem' },
}));

interface StyleProps {
  fileErrorStyle: boolean;
}

type DropZoneProps = {
  isSubmitting: boolean;
  setCsrf: (value: string) => void;
  csrf: string;
  files: SavedFile[];
  setFiles: (array: SavedFile[]) => void;
  submitAfterFiles: boolean;
  uploading: boolean;
  setUploading: (value: boolean) => void;
  fileErrorText?: string;
  gdpr: boolean;
};

const DropZone: React.FC<DropZoneProps> = ({
  isSubmitting,
  setCsrf,
  csrf,
  setFiles,
  submitAfterFiles,
  uploading,
  setUploading,
  files,
  fileErrorText,
  gdpr,
}: DropZoneProps) => {
  const { t } = useTranslation(['form', 'validation']);

  const [fileError, setFileError] = useState<string>();
  const classes = useStyles({ fileErrorStyle: !!fileError });
  const [loadingFile, setLoadingFile] = useState<SavedFile>();
  const [loadingArray, setLoadingArray] = useState<SavedFile[]>([]);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [loadingCount, setLoadingCount] = useState(0);
  const [fileQueue, setFileQueue] = useState<File[]>([]);
  const [newFile, setNewFile] = useState<File>();
  const maxFileSize: number = process.env.NEXT_PUBLIC_MAX_FILE_SIZE
    ? parseInt(process.env.NEXT_PUBLIC_MAX_FILE_SIZE)
    : 1000;
  const [uploadPercent, setUploadPercent] = useState(0);

  useEffect(() => {
    let sum = 0;

    if (loadingArray.length > 0) {
      sum = loadingArray.reduce((sum, a) => sum + a.uploadPercent, 0);
    }

    sum = (sum / (loadingCount * 100)) * 100;

    setUploadPercent(Math.floor(sum));
  }, [loadingArray]);

  useEffect(() => {
    if (fileErrorText) setFileError(fileErrorText);
  }, [fileErrorText]);

  useEffect(() => {
    if (uploadPercent >= 100) {
      setLoadingArray([]);
      setLoadingCount(0);
      setUploadPercent(0);
    }
  }, [uploadPercent]);

  useEffect(() => {
    if (loadingFile || fileQueue.length > 0) {
      if (!uploading) setUploading(true);
    }
  }, [loadingFile]);

  const callBack = useCallback(
    async (response: any) => {
      if (response.error) {
        setFileError(response.error);
        setNewFile(undefined);
        setLoadingFile(undefined);
        setLoadingCount(loadingCount - 1);
        return;
      }
      const savedFiles = await response.savedFiles;

      if (savedFiles) {
        setCsrf(response.newCsfrToken);
        setFiles([...files, ...savedFiles]);

        setNewFile(undefined);
        setLoadingFile(undefined);
        if (fileQueue.length === 0) {
          setUploading(false);
        }
      }
    },
    [files, fileQueue]
  );

  const loadingCallback = useCallback(
    (loadingFile: any) => {
      setLoadingFile(loadingFile);
      setLoadingArray([...loadingArray, loadingFile]);
    },
    [files]
  );

  useEffect(() => {
    setFiles(files);
  }, [files]);

  useEffect(() => {
    if (fileQueue && fileQueue.length > 0) {
      const arrayCopy = [...fileQueue];
      if (!newFile) {
        setNewFile(arrayCopy[0]);
        arrayCopy.splice(0, 1);
        setFileQueue(arrayCopy);
      }
    }
  }, [fileQueue, newFile]);

  useEffect(() => {
    if (newFile) {
      const uploadFile = async () => {
        const token = executeRecaptcha ? await executeRecaptcha('form') : '';
        try {
          const recaptchaCheck = await RecaptchaCheckCase(token);
          if (!recaptchaCheck) {
            //TODO Add error message
            return;
          }
          try {
            await FileUploadCase(
              newFile,
              csrf,
              recaptchaCheck.token,
              callBack,
              loadingCallback,
              setFileError,
              gdpr
            );
          } catch {
            //pass
          }
        } catch {
          //pass
        }
      };
      uploadFile();
    }
  }, [newFile]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const files = acceptedFiles.filter((file) => {
        if (!gdpr) if (excludeFromTransfer.includes(file.type)) return false;
        return allowedMimeTypes.includes(file.type);
      });

      const notAccepted = acceptedFiles.filter((file) => {
        if (!gdpr) return excludeFromTransfer.includes(file.type);
        return !allowedMimeTypes.includes(file.type);
      });

      if (notAccepted.length > 0) {
        setFileError('invalidFileFormat ' + notAccepted.map((file) => file.name));
      }

      const combinedFileSize = files.reduce((sum: number, a: File) => sum + a.size, 0);

      let uploadedFilesSize = 0;
      const loadingSize = loadingFile ? loadingFile.size : 0;
      let queueSize = 0;
      if (files && files.length > 0) {
        uploadedFilesSize = files.reduce((sum, a) => sum + a.size, 0);
      }

      if (fileQueue.length > 0) {
        queueSize = fileQueue.reduce((sum, a) => sum + a.size, 0);
      }

      const isFull = combinedFileSize + loadingSize + uploadedFilesSize + queueSize >= maxFileSize;

      if (combinedFileSize > maxFileSize) {
        setFileError('tooBig');
        return;
      }
      if (isFull) {
        setFileError('tooFull');
        return;
      } else {
        if (fileQueue.length > 0 || loadingArray.length > 0) {
          setLoadingCount(files.length + loadingCount);
        } else {
          setLoadingCount(files.length);
        }

        setFileQueue([...fileQueue, ...files]);
      }
    },
    [files, loadingFile, fileQueue]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: submitAfterFiles,
    multiple: true,
  });

  const removeFile = (file: SavedFile) => {
    removeFileCase(file.uuid);
    setFiles(files.filter((item) => item.uuid !== file.uuid));
  };

  const onClick = (e: React.MouseEvent<HTMLElement>, file: SavedFile) => {
    removeFile(file);
    e.stopPropagation();
  };

  const mappedFiles = files.map((file, i) => (
    <div style={{ display: 'flex' }} key={file.uuid + '-' + i}>
      <Typography className={classes.finishedText}>
        <span className={classes.uploadPercent}>{file.uploadPercent?.toFixed(0)}%</span>
        {file.originalName}
      </Typography>
      <Button
        disabled={isSubmitting || submitAfterFiles}
        className={classes.deleteButton}
        onClick={(e) => onClick(e, file)}
        type="button"
      >
        {t('dropzone.deleteFile')}
      </Button>
    </div>
  ));

  return (
    <div>
      <div className="dropzone" {...getRootProps()}>
        <input {...getInputProps()} />
        <Grid
          container
          alignItems="center"
          style={{ height: '100%' }}
          justifyContent="flex-end"
          direction="column"
        >
          <Grid item>
            {isDragActive ? (
              <Typography className={classes.dropzoneText}>{t('dropzone.dropFiles')}</Typography>
            ) : (
              <Typography className={classes.dropzoneText}>
                {t('dropzone.dragOrClick.p')}
                <span className={classes.dropzoneSpan}>{t('dropzone.dragOrClick.span')}</span>
              </Typography>
            )}
          </Grid>
          {gdpr && (
            <Typography className={classes.fileExtensionsGDPR}>
              {t('dropzone.acceptedFormats')} {gdprFileExtensions.join(', ')}
            </Typography>
          )}
          <CardMedia
            className={classes.arrowIcon}
            image="/static/images/upload_arrow.svg"
          ></CardMedia>
        </Grid>
        <Grid
          item
          container
          direction="column"
          justifyContent="flex-end"
          style={{ height: '100%', padding: 20 }}
        >
          <div
            role="button"
            tabIndex={-1}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
            style={{ cursor: 'initial' }}
            onClick={(e) => e.stopPropagation()}
          >
            {files && mappedFiles}
          </div>

          {loadingFile && (
            <Typography key={loadingFile.path} className={classes.loadginText}>
              <span className={classes.uploadPercent}>
                {loadingFile.uploadPercent?.toFixed(0)}%
              </span>
              {loadingFile.path}
            </Typography>
          )}

          {fileQueue &&
            fileQueue.map((file, i) => (
              <Typography key={file.name + i} className={classes.loadginText}>
                {file.name}
              </Typography>
            ))}
        </Grid>
      </div>

      <div className="dropzone-mobile-container">
        {gdpr && (
          <Typography className={classes.fileExtensionsGDPR}>
            {t('dropzone.acceptedFormats')} {gdprFileExtensions.join(', ')}
          </Typography>
        )}
        <div className="mobile-files-container" onClick={(e) => e.stopPropagation()}>
          {files && mappedFiles}
        </div>
        <div className="mobile-loading-container">
          {fileQueue &&
            fileQueue.map((file, i) => (
              <Typography key={file.name + i} className={classes.loadginText}>
                {file.name}
              </Typography>
            ))}
        </div>

        <div className="dropzone-mobile" {...getRootProps()}>
          <input {...getInputProps()} />
          <Button variant="contained" className={classes.mobileFileButton}>
            {t('dropzone.mobileButton')}
          </Button>
        </div>
      </div>
      {!!uploadPercent && (
        <Box className={classes.progressContainer}>
          <LinearProgress
            className={classes.progressBar}
            variant="determinate"
            classes={{ bar1Determinate: classes.progressColor }}
            value={uploadPercent}
          />
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minWidth: '35px',
            }}
          >
            <Typography className={classes.progressLabel}>{uploadPercent}%</Typography>
          </Box>
        </Box>
      )}
      {!gdpr && (
        <Typography className={classes.fileExtensions}>
          {t('dropzone.acceptedFormats')}{' '}
          {gdpr ? gdprFileExtensions.join(', ') : transferFileExtensions.join(', ')}
        </Typography>
      )}
      {fileError && (
        <Typography className={classes.fileError}>
          {t('dropzone.' + fileError.split(' ')[0]) +
            fileError.substring(fileError.split(' ')[0].length)}
        </Typography>
      )}
      <style jsx>{`
        .form-container {
          height: 100%;
          min-height: 100%;
          display: flex;
          flex-direction: column;
        }
        form {
          width: 100%;
        }
        .left-container {
          width: 100%;
        }
        .dropzone-mobile-container {
          width: 100%;
          min-height: 136px;
          border-radius: 4px;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          border: 1px dashed;

          align-items: center;
          padding: 25px 15px 25px 15px;
          border-color: ${fileError ? 'red' : '#00000080'};
        }
        .dropzone-mobile {
        }
        .dropzone {
          display: none;
        }
        .mobile-files-container {
          cursor: initial;
        }
        .mobile-loading-container {
          margin-bottom: 15px;
        }
        .button-container {
          display: flex;
          margin-top: 60px;
          align-items: center;
          justify-content: center;
          width: 100%;
        }
        .step {
          display: none;
        }
        @media only screen and (min-width: ${theme.breakpoints.values.sm}px) {
          .step {
            display: block;
            margin-top: 20px;
          }
          .dropzone-mobile-container {
            display: none;
          }
          .button-container {
            justify-content: flex-end;
          }
          .dropzone {
            display: flex;
            flex-wrap: nowrap;
            flex-direction: column;

            border: 1px dashed;
            justify-content: center;
            align-items: center;
            border-radius: 4px;
            flex: 1 1 auto;
            margin-top: 30px;
            cursor: pointer;
            border-color: ${fileError ? 'red' : '#00000080'};
          }
        }

        @media only screen and (min-width: ${theme.breakpoints.values.md}px) {
          form {
            width: initial;
          }
          .form-container {
            flex-direction: row;
          }
          .button-container {
            justify-content: flex-start;
          }
          .dropzone-mobile-container {
            display: none;
          }
          .dropzone {
            min-height: 655px;
            max-width: 579px;
          }
          .step {
            display: block;
          }
          .left-container {
            width: 100%;
            margin-right: 30px;
          }
        }
      `}</style>
    </div>
  );
};

export default DropZone;
